// apps/web/src/pages/Gallery/AddMusic.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { Button } from '../../../web/components/ui/button';
import { useTranslation } from 'react-i18next';
import type {FilterPresetId} from '../../../shared/components/filterHelpers.web';

type MusicTrack = {
  _id: string;
  title: string;
  artist: string;
  s3Url?: string;
  url?: string;
  audioUrl?: string;
  duration?: number;
};

type LocState = {
  editTargetId?: string;
  remoteUrl?: string;
  sidebarData?: any;
  videoUrl?: string;
};

const AddMusic: React.FC = () => {
  const { t } = useTranslation();

  const [musicTracks, setMusicTracks] = useState<MusicTrack[]>([]);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  const { mediaItems } = useMediaPreview();
  const srcFileRef = useRef<File | null>(null);
  const createdObjectUrlRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { editTargetId, remoteUrl, sidebarData, videoUrl: legacyVideoUrl } =
    ((location.state as any) || {}) as LocState;

  // 🔎 find the media item so we can read its filterId
  const mediaItem = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)
    : undefined;

  const getApiBaseUrl = () => {
    const env = process.env.REACT_APP_API_BASE_URL;
    return env ? env.replace(/\/api\/?$/, '') : 'http://localhost:3001';
  };

  const isHttp = (u?: string | null) => !!u && /^https?:\/\//i.test(u || '');
  const toHttp = (u?: string | null) =>
    !u ? undefined : isHttp(u) ? u : u!.startsWith('/') ? `${getApiBaseUrl()}${u}` : undefined;

  const toAbsoluteHttp = (u?: string | null) => {
    if (!u) return undefined;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith('/')) return `${getApiBaseUrl()}${u}`;
    return undefined;
  };

  //Helpers for Filtered Media

const fileSignature = (f?: File | null) =>
  f ? `${f.name}|${f.size}|${(f as any).lastModified || 0}` : 'none';

// What the user picked:
const selectedFilterId: FilterPresetId =
  (mediaItem?.filterId as FilterPresetId) ?? 'none';


  // Load music list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/music');
        if (!cancelled) setMusicTracks(res.data || []);
      } catch (err) {
        console.error('Failed to load tracks:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 🔎 Search
  const [query, setQuery] = useState('');
  const filteredTracks = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return musicTracks;
    return musicTracks.filter((t) => {
      const title = (t.title || '').toLowerCase();
      const artist = (t.artist || '').toLowerCase();
      return title.includes(q) || artist.includes(q);
    });
  }, [musicTracks, query]);

  // Resolve source video (prefer File in context)
useEffect(() => {
  const ctxFile = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)?.file
    : undefined;

  const sig = fileSignature(ctxFile);
  // If the file changed, rebuild object URL
  if (ctxFile instanceof File) {
    srcFileRef.current = ctxFile;

    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
    createdObjectUrlRef.current = URL.createObjectURL(ctxFile);
    setVideoPreviewUrl(createdObjectUrlRef.current);
  } else {
    // fall back to HTTP
    const httpFallback = toAbsoluteHttp(remoteUrl) || toAbsoluteHttp(legacyVideoUrl);
    srcFileRef.current = null;
    // Clean up any previous blob URL
    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
    setVideoPreviewUrl(httpFallback || null);
  }

  // cleanup on unmount
  return () => {
    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
  };
  // depend on the actual file identity + fallbacks
}, [editTargetId, mediaItems, remoteUrl, legacyVideoUrl]);


  // ⏺️ Choose a track
  const handleChoose = (t: MusicTrack) => {
    const ctxFile = editTargetId
      ? mediaItems.find((m) => m.id === editTargetId)?.file
      : undefined;

    const httpFallback =
      toHttp(videoPreviewUrl) || // undefined for blob:
      toHttp(remoteUrl) ||
      toHttp(legacyVideoUrl);

    navigate('/choose-track', {
      state: {
        editTargetId,
        sidebarData,
        videoFile: ctxFile ?? null, // pass File if available
        remoteUrl: ctxFile ? undefined : httpFallback, // HTTP(S) fallback only
        videoUrl: legacyVideoUrl,
        filterId: selectedFilterId,
        // pass track data
        track: {
          _id: t._id,
          title: t.title,
          artist: t.artist,
          audioUrl: t.audioUrl || t.url || t.s3Url,
          duration: t.duration,
        },
      },
    });
  };

 return (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      height: '100vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: '#000',
    }}
  >
    {/* Header */}
    <div style={{ padding: '16px 16px 8px' }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#ccc' }}>
        {t('gallery.addMusic.heading')}
      </h2>

      {/* Search */}
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('gallery.addMusic.searchPlaceholder')}
          style={{
            flex: 1,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #ccc',
            color: '#111',
            background: '#ddd',
            outline: 'none',
            fontSize: 16,
          }}
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #ccc',
              background: '#f2f2f2',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}
      </div>
    </div>

    {/* Music List */}
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {filteredTracks.length === 0 && (
        <div style={{ padding: 16, color: '#666' }}>
          {t('gallery.addMusic.noMatches', { query })}
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {filteredTracks.map((track) => (
          <li
            key={track._id}
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Link
                  to={`/musicdetails/${track._id}`}
                  style={{
                    fontWeight: 600,
                    color: '#666',
                    textDecoration: 'none',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {track.title}
                </Link>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {track.artist}
                </div>
              </div>

              <Button
                onClick={() => handleChoose(track)}
                style={{
                  backgroundColor: '#2563eb',
                  color: '#ccc',
                  border: 'none',
                  padding: '8px 12px',
                  height: 40,
                  flexShrink: 0,
                }}
              >
                {t('gallery.addMusic.choose')}
              </Button>
            </div>

            <audio
              src={track.s3Url || track.audioUrl || track.url}
              controls
              style={{ width: '100%' }}
            />
          </li>
        ))}
      </ul>
    </div>
  </div>
);

};

export default AddMusic;
